import {ExtrasGraphicIcon, PriceGraphicIcon, FamilyGraphicIcon, SingleGraphicIcon, IconType, CardGraphicIcon, HealthTestGraphicIcon} from '@nib/icons';
import {IWHIPageIndex, IWHIPageList, SHOW_PRODUCT_PAGE} from '../../constants';
import {IWHIPage} from '@nib/types-session-api';
import {withPrefix} from 'gatsby';

export interface ProgressStep {
  title: string;
  isAccessed: boolean;
  isDisabled: boolean;
  isComplete: boolean;
  href: string;
  icon: IconType;
}

const COVER_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.COVER_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: '/', // there's two pages that point to the same cover details component, treat the root page as the correct one and link to that instead of /hospital-extras-cover
  icon: ExtrasGraphicIcon
};

const FAMILY_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.FAMILY_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: withPrefix(IWHIPageList[IWHIPageIndex.FAMILY_DETAILS].link),
  icon: FamilyGraphicIcon
};

const HEALTH_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.HEALTH_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: withPrefix(IWHIPageList[IWHIPageIndex.HEALTH_DETAILS].link),
  icon: HealthTestGraphicIcon
};

const PAYMENT_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.PAYMENT_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: withPrefix(IWHIPageList[IWHIPageIndex.PAYMENT_DETAILS].link),
  icon: PriceGraphicIcon
};

const VISA_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.VISA_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: withPrefix(IWHIPageList[IWHIPageIndex.VISA_DETAILS].link),
  icon: CardGraphicIcon
};

const YOUR_DETAILS_STEP: ProgressStep = {
  title: IWHIPageList[IWHIPageIndex.YOUR_DETAILS].title,
  isAccessed: false,
  isDisabled: false,
  isComplete: false,
  href: withPrefix(IWHIPageList[IWHIPageIndex.YOUR_DETAILS].link),
  icon: SingleGraphicIcon
};

export const getPageStepIndex = (title: string, progressSteps: ProgressStep[]) => {
  let stepIndex = -1;

  for (let i = 0; i < progressSteps.length; i++) {
    if (progressSteps[i].title === title) {
      stepIndex = i;
      break;
    }
  }

  return stepIndex;
};

export const clone = (obj: any) => Object.assign({}, obj);

export const getProgressSteps = () => {
  if (SHOW_PRODUCT_PAGE) {
    return [clone(COVER_DETAILS_STEP), clone(YOUR_DETAILS_STEP), clone(FAMILY_DETAILS_STEP), clone(HEALTH_DETAILS_STEP), clone(VISA_DETAILS_STEP), clone(PAYMENT_DETAILS_STEP)];
  } else {
    return [clone(YOUR_DETAILS_STEP), clone(FAMILY_DETAILS_STEP), clone(HEALTH_DETAILS_STEP), clone(VISA_DETAILS_STEP), clone(PAYMENT_DETAILS_STEP)];
  }
};

export interface ProgressStepsArguments {
  currentPage: IWHIPage;
  lastCompletedPage: IWHIPage;
}

export const enableStep = (step: ProgressStep) => {
  if (step) {
    step.isDisabled = false;
    step.isAccessed = true;
  }
};

export const disableStep = (step: ProgressStep) => {
  step.isDisabled = true;
  step.isAccessed = false;
};

/**
 * Given the current page and the last completed page, determine if the provided step should be marked as accessed
 */
export const shouldPageBeMarkedAccessed = (lastCompletedPageIndex: number, currentPageStepIndex: number, stepIndex: number) => {
  const lastCompletedPageIndexInclActive = lastCompletedPageIndex + 1;
  // Either a page is marked as accessed because the "LastCompletedPage" from the session indicates it or a dependancy was completed or
  // or in the absense of this, the current step index is a precursor page to the current page.
  return (lastCompletedPageIndexInclActive >= currentPageStepIndex && stepIndex <= lastCompletedPageIndexInclActive) || stepIndex <= currentPageStepIndex;
};

/**
 * Given the appropriate progress steps for the selected scale, impart the current context of the funnel (selected products, progress etc.) onto the steps
 * @param progressSteps
 * @param params
 */
export const transformProgressSteps = (progressSteps: ProgressStep[], params: ProgressStepsArguments): ProgressStep[] => {
  const {currentPage, lastCompletedPage} = params;
  const currentPageStepIndex = getPageStepIndex(currentPage.title || '', progressSteps);
  const lastCompletedPageIndex = getPageStepIndex(lastCompletedPage.title ?? '', progressSteps);

  const structure = progressSteps.slice();
  for (let i = 0; i < structure.length; i++) {
    const step = structure[i];
    if (i > currentPageStepIndex && !shouldPageBeMarkedAccessed(lastCompletedPageIndex, currentPageStepIndex, i)) {
      disableStep(step);
    } else {
      enableStep(step);
    }
  }

  return structure;
};
